import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { placeOrderApi } from '../../api/orderApi';
import { getAddressesApi, addAddressApi } from '../../api/userApi';
import imgUrl from '../../utils/imgUrl';
import { applyCouponApi } from '../../api/cartApi';
import { fetchCart, clearCart } from '../../store/slices/cartSlice';
import useIsMobile from '../../hooks/useIsMobile';

const STEPS = ['Address', 'Delivery', 'Payment', 'Review'];

export default function Checkout() {
  const dispatch  = useDispatch();
  const nav       = useNavigate();
  const { items, subtotal } = useSelector((s) => s.cart);
  const isMobile  = useIsMobile();
  const [step, setStep]           = useState(0);
  const [addresses, setAddresses] = useState([]);
  const [selAddr, setSelAddr]     = useState(null);
  const [delivery, setDelivery]   = useState('standard');
  const [payment, setPayment]     = useState('cod');
  const [coupon, setCoupon]       = useState('');
  const [couponData, setCouponData] = useState(null);
  const [placing, setPlacing]     = useState(false);
  const [newAddr, setNewAddr]     = useState({ full_name:'', phone:'', address_line1:'', address_line2:'', city:'', state:'', postal_code:'', country:'India', type:'shipping' });

  const shipping = delivery === 'express' ? 149 : parseFloat(subtotal) > 999 ? 0 : 99;
  const discount = couponData ? (couponData.type === 'flat' ? couponData.discount_value : parseFloat(subtotal) * couponData.discount_value / 100) : 0;
  const total    = (parseFloat(subtotal) + shipping - discount).toFixed(2);

  useEffect(() => {
    dispatch(fetchCart());
    getAddressesApi().then((r) => { setAddresses(r.data); if (r.data.length) setSelAddr(r.data[0].id); }).catch(() => {});
  }, [dispatch]);

  const handleAddAddress = async (e) => {
    e.preventDefault();
    try {
      const res = await addAddressApi({ ...newAddr, is_default: !addresses.length });
      setAddresses((prev) => [...prev, res.data]);
      setSelAddr(res.data.id);
      toast.success('Address saved');
    } catch { toast.error('Failed to save address'); }
  };

  const handleCoupon = async () => {
    try {
      const res = await applyCouponApi({ code: coupon });
      setCouponData(res.data.coupon);
      toast.success('Coupon applied!');
    } catch (e) { toast.error(e.response?.data?.message || 'Invalid coupon'); }
  };

  const handlePlaceOrder = async () => {
    if (!selAddr) return toast.error('Please select a delivery address');
    setPlacing(true);
    try {
      const res = await placeOrderApi({ shipping_address_id: selAddr, billing_address_id: selAddr, payment_method: payment });
      dispatch(clearCart());
      toast.success('Order placed successfully!');
      nav(`/order-success/${res.data.order_id}`);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to place order'); }
    finally { setPlacing(false); }
  };

  if (!items.length) { nav('/cart'); return null; }

  return (
    <div className="page-top">
      {/* Hero */}
      <div style={{ ...S.hero, padding: isMobile ? '28px 18px' : '48px 56px' }}>
        <p className="sec-label">Secure Checkout</p>
        <h1 style={S.heroTitle}>CHECKOUT</h1>
      </div>

      {/* Steps */}
      <div style={{ ...S.stepBar, padding: isMobile ? '14px 18px' : '20px 56px' }}>
        {STEPS.map((s, i) => (
          <div key={s} style={S.stepItem}>
            <div style={{ ...S.stepNum, background: i <= step ? 'var(--red)' : 'var(--border)', color: i <= step ? '#fff' : '#aaa' }}>{i + 1}</div>
            {!isMobile && <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: i <= step ? 'var(--black)' : '#aaa' }}>{s}</span>}
            {i < STEPS.length - 1 && <div style={S.stepLine} />}
          </div>
        ))}
      </div>

      <div style={{ ...S.wrap, gridTemplateColumns: isMobile ? '1fr' : '1fr 320px', padding: isMobile ? '20px 18px' : '36px 56px', gap: isMobile ? '20px' : '32px' }}>
        {/* Left — Steps Content */}
        <div style={S.left}>

          {/* STEP 0: Address */}
          {step === 0 && (
            <div style={S.card}>
              <h2 style={S.cardTitle}>DELIVERY ADDRESS</h2>
              {addresses.map((a) => (
                <div key={a.id} onClick={() => setSelAddr(a.id)}
                  style={{ ...S.addrTile, borderColor: selAddr === a.id ? 'var(--red)' : 'var(--border)', background: selAddr === a.id ? '#fff9f9' : '#fff' }}>
                  <div style={{ ...S.radio, borderColor: selAddr === a.id ? 'var(--red)' : '#ccc' }}>
                    {selAddr === a.id && <div style={S.radioDot} />}
                  </div>
                  <div>
                    <p style={{ fontWeight: 700, marginBottom: '2px' }}>{a.full_name} · {a.phone}</p>
                    <p style={{ fontSize: '13px', color: '#555', lineHeight: 1.6 }}>{a.address_line1}{a.address_line2 ? ', ' + a.address_line2 : ''}, {a.city}, {a.state} — {a.postal_code}</p>
                  </div>
                </div>
              ))}

              <p style={S.sectionLabel}>Add New Address</p>
              <form onSubmit={handleAddAddress}>
                <div style={S.formRow}>
                  <div><label style={S.lbl}>Full Name</label><input style={S.inp} value={newAddr.full_name} onChange={(e) => setNewAddr({ ...newAddr, full_name: e.target.value })} required /></div>
                  <div><label style={S.lbl}>Phone</label><input style={S.inp} value={newAddr.phone} onChange={(e) => setNewAddr({ ...newAddr, phone: e.target.value })} required /></div>
                </div>
                <label style={S.lbl}>Address Line 1</label>
                <input style={S.inp} placeholder="House no, Street, Area" value={newAddr.address_line1} onChange={(e) => setNewAddr({ ...newAddr, address_line1: e.target.value })} required />
                <label style={S.lbl}>Address Line 2 (optional)</label>
                <input style={S.inp} placeholder="Landmark, Colony" value={newAddr.address_line2} onChange={(e) => setNewAddr({ ...newAddr, address_line2: e.target.value })} />
                <div style={S.formRow}>
                  <div><label style={S.lbl}>City</label><input style={S.inp} value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} required /></div>
                  <div><label style={S.lbl}>State</label><input style={S.inp} value={newAddr.state} onChange={(e) => setNewAddr({ ...newAddr, state: e.target.value })} required /></div>
                  <div><label style={S.lbl}>PIN Code</label><input style={S.inp} value={newAddr.postal_code} onChange={(e) => setNewAddr({ ...newAddr, postal_code: e.target.value })} required /></div>
                </div>
                <button type="submit" style={S.addBtn}>Save Address</button>
              </form>

              <button style={S.nextBtn} onClick={() => { if (!selAddr) { toast.error('Select an address'); return; } setStep(1); }}>Continue to Delivery →</button>
            </div>
          )}

          {/* STEP 1: Delivery */}
          {step === 1 && (
            <div style={S.card}>
              <h2 style={S.cardTitle}>DELIVERY METHOD</h2>
              {[
                { key: 'standard', name: 'Standard Delivery', sub: '5–7 business days', price: parseFloat(subtotal) > 999 ? 'FREE' : '₹99', badge: null },
                { key: 'express',  name: 'Express Delivery',  sub: '2–3 business days', price: '₹149', badge: 'FAST' },
              ].map((d) => (
                <div key={d.key} onClick={() => setDelivery(d.key)}
                  style={{ ...S.dtile, borderColor: delivery === d.key ? 'var(--red)' : 'var(--border)', background: delivery === d.key ? '#fff9f9' : '#fff', marginBottom: '10px' }}>
                  <div style={{ ...S.radio, borderColor: delivery === d.key ? 'var(--red)' : '#ccc' }}>
                    {delivery === d.key && <div style={S.radioDot} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                      <span style={{ fontWeight: 700, fontSize: '13px' }}>{d.name}</span>
                      {d.badge && <span style={{ background: 'var(--green)', color: '#fff', fontSize: '9px', fontWeight: 700, letterSpacing: '1px', padding: '2px 7px' }}>{d.badge}</span>}
                    </div>
                    <p style={{ fontSize: '12px', color: '#888' }}>{d.sub}</p>
                  </div>
                  <span style={{ fontWeight: 700, color: 'var(--red)', fontSize: '13px' }}>{d.price}</span>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button style={S.backBtn} onClick={() => setStep(0)}>← Back</button>
                <button style={S.nextBtn} onClick={() => setStep(2)}>Continue to Payment →</button>
              </div>
            </div>
          )}

          {/* STEP 2: Payment */}
          {step === 2 && (
            <div style={S.card}>
              <h2 style={S.cardTitle}>PAYMENT METHOD</h2>
              <div style={S.payTabs}>
                {[['cod','💵 COD'],['upi','📱 UPI'],['card','💳 Card']].map(([key, label]) => (
                  <button key={key} style={{ ...S.ptab, background: payment === key ? 'var(--black)' : 'none', color: payment === key ? '#fff' : '#888' }}
                    onClick={() => setPayment(key)}>{label}</button>
                ))}
              </div>

              {payment === 'cod' && (
                <div style={S.codBox}>
                  <p style={{ fontWeight: 700, marginBottom: '6px' }}>Cash on Delivery</p>
                  <p style={{ fontSize: '13px', color: '#555', lineHeight: 1.7 }}>Pay in cash when your order arrives. Additional ₹20 COD fee may apply.</p>
                </div>
              )}
              {payment === 'upi' && (
                <div>
                  <label style={S.lbl}>UPI ID</label>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                    <input style={{ ...S.inp, flex: 1 }} placeholder="yourname@upi" />
                    <button style={S.addBtn}>Verify</button>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {['GPay','PhonePe','Paytm','BHIM'].map((app) => (
                      <div key={app} style={S.upiApp}>{app}</div>
                    ))}
                  </div>
                </div>
              )}
              {payment === 'card' && (
                <div>
                  <label style={S.lbl}>Card Number</label>
                  <input style={S.inp} placeholder="1234 5678 9012 3456" maxLength={19} />
                  <div style={S.formRow}>
                    <div><label style={S.lbl}>Expiry</label><input style={S.inp} placeholder="MM / YY" /></div>
                    <div><label style={S.lbl}>CVV</label><input style={S.inp} placeholder="•••" maxLength={4} /></div>
                    <div><label style={S.lbl}>Name on Card</label><input style={S.inp} placeholder="As on card" /></div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                    {['VISA','MC','RuPay','AMEX'].map((b) => (
                      <span key={b} style={S.cardBrand}>{b}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Coupon */}
              <div style={S.couponRow}>
                <input style={{ ...S.inp, flex: 1, marginBottom: 0 }} placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} />
                <button style={S.addBtn} onClick={handleCoupon}>Apply</button>
              </div>
              {couponData && <p style={{ fontSize: '12px', color: 'var(--green)', fontWeight: 600, marginTop: '6px' }}>✓ Coupon applied — saving ₹{discount.toFixed(2)}</p>}

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button style={S.backBtn} onClick={() => setStep(1)}>← Back</button>
                <button style={S.nextBtn} onClick={() => setStep(3)}>Review Order →</button>
              </div>
            </div>
          )}

          {/* STEP 3: Review */}
          {step === 3 && (
            <div style={S.card}>
              <h2 style={S.cardTitle}>REVIEW YOUR ORDER</h2>
              {items.map((item) => (
                <div key={item.id} style={S.reviewItem}>
                  <div style={S.reviewImg}>
                    {item.image ? <img src={imgUrl(item.image)} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '👕'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontWeight: 600 }}>{item.name}</p>
                    <p style={{ fontSize: '12px', color: '#888' }}>Qty: {item.quantity}</p>
                  </div>
                  <p style={{ fontWeight: 700 }}>₹{(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
              <div style={{ marginTop: '16px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '6px' }}>
                  📦 Delivery: <strong>{delivery === 'express' ? 'Express (2–3 days)' : 'Standard (5–7 days)'}</strong>
                </p>
                <p style={{ fontSize: '13px', color: '#666' }}>
                  💳 Payment: <strong>{payment.toUpperCase()}</strong>
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button style={S.backBtn} onClick={() => setStep(2)}>← Back</button>
                <button style={{ ...S.nextBtn, background: 'var(--green)' }} onClick={handlePlaceOrder} disabled={placing}>
                  {placing ? 'Placing Order...' : '✓ PLACE ORDER'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right — Order Summary */}
        <div style={{ ...S.summary, position: isMobile ? 'relative' : 'sticky', top: isMobile ? undefined : '120px', order: isMobile ? -1 : undefined }}>
          <p style={S.summaryTitle}>ORDER SUMMARY</p>
          {items.map((item) => (
            <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px' }}>
              <span>{item.name} <span style={{ color: '#aaa' }}>×{item.quantity}</span></span>
              <span>₹{(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
          <div style={S.divider} />
          <div style={S.summRow}><span>Subtotal</span><span>₹{subtotal}</span></div>
          <div style={S.summRow}><span>Shipping</span><span style={{ color: shipping === 0 ? 'var(--green)' : 'inherit' }}>{shipping === 0 ? 'FREE' : `₹${shipping}`}</span></div>
          {discount > 0 && <div style={{ ...S.summRow, color: 'var(--green)' }}><span>Discount</span><span>−₹{discount.toFixed(2)}</span></div>}
          <div style={S.divider} />
          <div style={{ ...S.summRow, fontFamily: 'var(--font-display)', fontSize: '22px', letterSpacing: '1px' }}>
            <span>TOTAL</span><span>₹{total}</span>
          </div>
          <div style={S.secureNote}>
            🔒 100% Secure & Encrypted Payment
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  hero:        { background: 'var(--black)', padding: '48px 56px', borderBottom: '3px solid var(--gold)' },
  heroTitle:   { fontFamily: 'var(--font-display)', fontSize: 'clamp(32px,5vw,52px)', letterSpacing: '2px', color: '#fff', lineHeight: 1 },
  stepBar:     { display: 'flex', alignItems: 'center', padding: '20px 56px', background: '#fff', borderBottom: '1px solid var(--border)', gap: '0' },
  stepItem:    { display: 'flex', alignItems: 'center', gap: '8px', flex: 1 },
  stepNum:     { width: '26px', height: '26px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, flexShrink: 0 },
  stepLine:    { flex: 1, height: '1px', background: 'var(--border)', marginLeft: '8px' },
  wrap:        { display: 'grid', gridTemplateColumns: '1fr 320px', gap: '32px', padding: '36px 56px', maxWidth: '1200px', margin: '0 auto' },
  left:        {},
  card:        { background: '#fff', border: '1px solid var(--border)', padding: '28px' },
  cardTitle:   { fontFamily: 'var(--font-display)', fontSize: '18px', letterSpacing: '2px', marginBottom: '20px', borderBottom: '2px solid var(--red)', paddingBottom: '10px' },
  addrTile:    { border: '1.5px solid', padding: '14px 16px', marginBottom: '12px', cursor: 'pointer', display: 'flex', gap: '12px', alignItems: 'flex-start' },
  radio:       { width: '17px', height: '17px', border: '2px solid', borderRadius: '50%', flexShrink: 0, marginTop: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  radioDot:    { width: '7px', height: '7px', background: 'var(--red)', borderRadius: '50%' },
  sectionLabel:{ fontFamily: 'var(--font-display)', fontSize: '14px', letterSpacing: '2px', margin: '20px 0 14px', color: '#888' },
  formRow:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: '10px', marginBottom: '0' },
  lbl:         { display: 'block', fontSize: '10px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: '#777', marginBottom: '4px', marginTop: '10px' },
  inp:         { width: '100%', border: '1.5px solid var(--border)', background: 'var(--offwhite)', padding: '9px 12px', fontFamily: 'var(--font-body)', fontSize: '13px', outline: 'none', boxSizing: 'border-box', marginBottom: '0' },
  addBtn:      { background: 'var(--black)', color: '#fff', border: 'none', padding: '9px 18px', fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', marginTop: '12px' },
  nextBtn:     { flex: 1, padding: '13px', background: 'var(--red)', color: '#fff', border: 'none', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer' },
  backBtn:     { padding: '13px 20px', background: '#fff', color: 'var(--black)', border: '1.5px solid var(--border)', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
  dtile:       { border: '1.5px solid', padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' },
  payTabs:     { display: 'flex', border: '1.5px solid var(--border)', marginBottom: '18px', overflow: 'hidden' },
  ptab:        { flex: 1, border: 'none', borderRight: '1px solid var(--border)', padding: '10px 6px', fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', cursor: 'pointer', transition: 'all .2s' },
  codBox:      { background: 'var(--light)', border: '1px solid var(--border)', borderLeft: '3px solid var(--gold)', padding: '14px', marginBottom: '12px' },
  upiApp:      { background: 'var(--light)', border: '1.5px solid var(--border)', padding: '8px 14px', cursor: 'pointer', fontSize: '11px', fontWeight: 700 },
  cardBrand:   { background: 'var(--light)', border: '1px solid var(--border)', padding: '3px 9px', fontSize: '9px', fontWeight: 700, letterSpacing: '.5px' },
  couponRow:   { display: 'flex', gap: '8px', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border)', alignItems: 'flex-end' },
  reviewItem:  { display: 'flex', alignItems: 'center', gap: '14px', paddingBottom: '12px', marginBottom: '12px', borderBottom: '1px solid var(--border)' },
  reviewImg:   { width: '56px', height: '70px', background: 'var(--light)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', fontSize: '24px' },
  summary:     { background: '#fff', border: '1px solid var(--border)', padding: '24px', height: 'fit-content', position: 'sticky', top: '120px' },
  summaryTitle:{ fontFamily: 'var(--font-display)', fontSize: '16px', letterSpacing: '3px', marginBottom: '16px', borderBottom: '2px solid var(--red)', paddingBottom: '10px' },
  summRow:     { display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '10px' },
  divider:     { height: '1px', background: 'var(--border)', margin: '14px 0' },
  secureNote:  { fontSize: '11px', color: '#888', textAlign: 'center', marginTop: '14px', padding: '10px', background: 'var(--light)', fontWeight: 600 },
};
